import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, LogOut, Crown, Bell, 
  Leaf, BarChart3, Settings as SettingsIcon, ChevronRight,
  Target, Trash2, Plus, Check, Clock, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import SubscriptionSection from '../components/SubscriptionSection';
import DeleteAccountSection from '../components/DeleteAccountSection';
import LegalLinks from '../components/LegalLinks';
import SignInMethodsSection from '../components/SignInMethodsSection';
import { getAnalyticsConsent, setAnalyticsConsent } from '../lib/analytics';
import { isNative, scheduleDailyReminder, cancelDailyReminder, hapticLight } from '../lib/nativeFeel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Device-only daily notification preference (no server involvement).
const REMINDER_PREF_KEY = 'futureself_daily_reminder';
const loadReminderPref = () => {
  try {
    return { enabled: false, time: '08:00', ...JSON.parse(localStorage.getItem(REMINDER_PREF_KEY) || '{}') };
  } catch {
    return { enabled: false, time: '08:00' };
  }
};
const saveReminderPref = (pref) => {
  try { localStorage.setItem(REMINDER_PREF_KEY, JSON.stringify(pref)); } catch { /* ignore */ }
};

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout, activeGoalId, activateGoal, setActiveGoalId } = useAuth();
  const [goals, setGoals] = useState([]);
  const [reminderSettings, setReminderSettings] = useState(loadReminderPref);
  const [loading, setLoading] = useState(true);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [switchingGoal, setSwitchingGoal] = useState(null);
  const [savingReminderToggle, setSavingReminderToggle] = useState(false);
  // Analytics consent toggle mirror. `null` means user has not yet chosen.
  const [analyticsConsent, setAnalyticsConsentState] = useState(getAnalyticsConsent());

  // Always open Settings at the very top — never mid-page (e.g. near the
  // Delete Account section). Uses useLayoutEffect so it runs before paint,
  // plus a delayed pass to catch async section growth (Subscription card /
  // Goals list finish loading after mount and can shift layout).
  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      try { window.history.scrollRestoration = 'manual'; } catch { /* noop */ }
    }
    const scrollTop = () => {
      // Home's goal title deep-links to #goals-section; don't fight that scroll.
      if (window.location.hash === '#goals-section') return;
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        // If a parent element scrolls (rare, but the app has used dvh
        // layouts before), reset it too.
        const main = document.querySelector('main');
        if (main) main.scrollTop = 0;
      } catch { /* noop */ }
    };
    scrollTop();
    // Follow-up passes at 0, 60, 200, 500 ms so late-loading sections
    // (goals list, subscription status) can't shift us mid-load.
    const t1 = setTimeout(scrollTop, 60);
    const t2 = setTimeout(scrollTop, 200);
    const t3 = setTimeout(scrollTop, 500);
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      if ('scrollRestoration' in window.history) {
        try { window.history.scrollRestoration = 'auto'; } catch { /* noop */ }
      }
    };
  }, []);

  // Fetch goals with current active state
  const fetchGoals = useCallback(async () => {
    setGoalsLoading(true);
    try {
      const goalsRes = await fetch(`${API_URL}/api/goals`, {
        credentials: 'include',
      });
      if (goalsRes.ok) {
        const goalsData = await goalsRes.json();
        setGoals(goalsData);
        
        // Sync activeGoalId from goals if not set in context
        // Find the goal marked as active from the server
        const serverActiveGoal = goalsData.find(g => g.is_active);
        if (serverActiveGoal && !activeGoalId) {
          setActiveGoalId(serverActiveGoal.goal_id);
        }
      }
    } catch (error) {
      console.error('[Settings] Failed to fetch goals:', error);
    } finally {
      setGoalsLoading(false);
    }
  }, [API_URL, activeGoalId, setActiveGoalId]);

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchGoals();
      setLoading(false);
    };
    fetchData();
  }, [fetchGoals]); // Only run once on mount

  // Re-fetch goals when activeGoalId changes (e.g., after switching)
  useEffect(() => {
    if (!loading && activeGoalId) {
      fetchGoals();
    }
  }, [activeGoalId, loading, fetchGoals]); // eslint-disable-line react-hooks/exhaustive-deps

  // Home's goal title links here; scroll to goal switching once goals have rendered
  // (wait for `loading` to clear, otherwise #goals-section is not in the DOM yet).
  useEffect(() => {
    if (!loading && window.location.hash === '#goals-section' && goals.length) {
      document.getElementById('goals-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [goals.length, loading]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('[Settings] logout cleanup error:', error);
    } finally {
      toast.success('Logged out successfully');
      navigate('/login', { replace: true });
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/goals/${goalId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Goal deleted');
        await fetchGoals();
        if (activeGoalId === goalId) {
          navigate('/onboarding');
        }
      } else {
        throw new Error('Failed to delete goal');
      }
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const handleSetActiveGoal = async (goalId) => {
    // Prevent duplicate clicks or switching to already active goal
    if (goalId === activeGoalId || switchingGoal) {
      return;
    }
    
    setSwitchingGoal(goalId);
    
    try {
      const result = await activateGoal(goalId);
      
      if (result) {
        // Immediately update local goals state to reflect the change
        setGoals(prevGoals => prevGoals.map(g => ({
          ...g,
          is_active: g.goal_id === goalId
        })));
        
        toast.success(`Switched to "${result.goal?.title || 'goal'}"`);
        hapticLight();
      } else {
        throw new Error('Failed to activate goal');
      }
    } catch (error) {
      toast.error('Failed to switch goal');
      // Refresh goals to get correct state
      await fetchGoals();
    } finally {
      setSwitchingGoal(null);
    }
  };

  const handleReminderToggle = async (enabled) => {
    // Prevent rapid duplicate taps (double-tap on iOS, keyboard repeats, etc.).
    if (savingReminderToggle) return;
    setSavingReminderToggle(true);
    try {
      if (enabled) {
        const res = await scheduleDailyReminder(reminderSettings.time);
        if (!res?.scheduled) {
          toast.error("We couldn't set the device reminder. Please check notification permissions in your device settings.");
          return;
        }
        toast.success('Daily reminder set on this device');
      } else {
        await cancelDailyReminder();
        toast.success('Daily reminder cancelled');
      }
      const next = { ...reminderSettings, enabled };
      setReminderSettings(next);
      saveReminderPref(next);
    } catch (error) {
      toast.error("We couldn't update your reminder. Please try again.");
    } finally {
      setSavingReminderToggle(false);
    }
  };

  const handleTimeChange = async (time) => {
    const next = { ...reminderSettings, time };
    setReminderSettings(next);
    saveReminderPref(next);
    if (next.enabled) {
      try {
        await scheduleDailyReminder(time);
      } catch (error) {
        console.error('[Settings] Failed to reschedule reminder:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#8BAA9E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1C1E] pb-24" data-testid="settings-page">
      {/* Header */}
      <header className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 rounded-full bg-[#252528] flex items-center justify-center text-[#B5B1AB] hover:text-[#DCD3CA]"
            data-testid="back-btn"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div>
            <p className="text-[#8BAA9E] text-xs tracking-[0.2em] uppercase font-sans mb-1">Account</p>
            <h1 className="font-serif text-2xl text-[#DCD3CA]">Settings</h1>
          </div>
        </div>
      </header>

      <div className="px-6 space-y-6">
        {/* Profile Card */}
        <div className="bg-[#252528] border border-white/5 rounded-[24px] p-6" data-testid="profile-card">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#8BAA9E]/20 flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-[#8BAA9E]" strokeWidth={1.5} />
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-serif text-xl text-[#DCD3CA]">{user?.name}</h3>
              <p className="text-[#B5B1AB] text-sm font-sans">{user?.email}</p>
            </div>
            {user?.is_premium && (
              <div className="px-3 py-1 rounded-full bg-[#C6A75E]/20 text-[#C6A75E] text-xs font-sans flex items-center gap-1">
                <Crown className="w-3 h-3" />
                Premium
              </div>
            )}
          </div>
        </div>

        {/* Goals Section */}
        <div id="goals-section" className="bg-[#252528] border border-white/5 rounded-[24px] p-6" data-testid="goals-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[#8BAA9E]" strokeWidth={1.5} />
              <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">Your Goals</span>
            </div>
            <div className="flex items-center gap-2">
              {goalsLoading && (
                <Loader2 className="w-4 h-4 text-[#8BAA9E] animate-spin" />
              )}
              {user?.is_premium && (
                <span className="text-[#7A7670] text-xs font-sans">{goals.length} goal{goals.length !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>

          {/* Premium users: Show instructions for switching */}
          {user?.is_premium && goals.length > 1 && (
            <p className="text-[#7A7670] text-xs font-sans mb-3 px-1">
              Tap the <Check className="w-3 h-3 inline text-[#8BAA9E]" /> button to switch your active goal
            </p>
          )}

          <div className="space-y-3">
            {goals.map((goal) => {
              // Determine if this goal is active - prioritize context activeGoalId, fallback to goal.is_active
              const isActive = activeGoalId 
                ? goal.goal_id === activeGoalId 
                : goal.is_active === true;
              const isSwitching = switchingGoal === goal.goal_id;
              const canSwitch = user?.is_premium && !isActive && !switchingGoal;
              
              return (
                <div 
                  key={goal.goal_id}
                  data-testid={`goal-item-${goal.goal_id}`}
                  className={`p-4 rounded-2xl border-2 transition-all duration-200 ${
                    isActive 
                      ? 'bg-[#8BAA9E]/10 border-[#8BAA9E]/50' 
                      : 'bg-[#1C1C1E]/50 border-white/5 hover:border-white/10'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-serif text-lg text-[#DCD3CA] truncate">{goal.title}</h4>
                        {isActive && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#8BAA9E]/20 text-[#8BAA9E] text-[10px] font-sans font-medium whitespace-nowrap">
                            <Check className="w-3 h-3" />
                            ACTIVE
                          </span>
                        )}
                      </div>
                      <p className="text-[#B5B1AB] text-sm font-sans line-clamp-1">{goal.description}</p>
                    </div>
                    
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Switch/Activate Button - Always visible for non-active goals if premium */}
                      {user?.is_premium && !isActive && (
                        <button
                          onClick={() => handleSetActiveGoal(goal.goal_id)}
                          disabled={!canSwitch}
                          className={`
                            flex items-center justify-center w-9 h-9 rounded-full 
                            transition-all duration-200
                            ${isSwitching 
                              ? 'bg-[#8BAA9E]/30 cursor-wait' 
                              : canSwitch 
                                ? 'bg-[#8BAA9E]/20 hover:bg-[#8BAA9E]/40 text-[#8BAA9E] hover:scale-105' 
                                : 'bg-[#8BAA9E]/10 text-[#8BAA9E]/50 cursor-not-allowed'
                            }
                          `}
                          title="Set as active goal"
                          data-testid={`activate-goal-${goal.goal_id}`}
                        >
                          {isSwitching ? (
                            <Loader2 className="w-4 h-4 animate-spin text-[#8BAA9E]" />
                          ) : (
                            <Check className="w-5 h-5" />
                          )}
                        </button>
                      )}
                      
                      {/* Active indicator for the current goal (no button needed) */}
                      {isActive && (
                        <div 
                          className="flex items-center justify-center w-9 h-9 rounded-full bg-[#8BAA9E]/30"
                          title="Currently active"
                          data-testid={`active-indicator-${goal.goal_id}`}
                        >
                          <Check className="w-5 h-5 text-[#8BAA9E]" />
                        </div>
                      )}
                      
                      {/* Delete Button */}
                      <button
                        onClick={() => handleDeleteGoal(goal.goal_id)}
                        disabled={switchingGoal !== null}
                        className={`
                          flex items-center justify-center w-9 h-9 rounded-full 
                          transition-all duration-200
                          ${switchingGoal !== null 
                            ? 'text-[#CF6679]/50 cursor-not-allowed' 
                            : 'text-[#CF6679] hover:bg-[#CF6679]/20'
                          }
                        `}
                        title="Delete goal"
                        data-testid={`delete-goal-${goal.goal_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add New Goal Button - Premium users can always add, free users only if no goals */}
          {(user?.is_premium || goals.length === 0) && (
            <Button
              onClick={() => navigate('/onboarding')}
              variant="outline"
              className="w-full mt-4 border-dashed border-[#666666] text-[#B5B1AB] hover:border-[#8BAA9E] hover:text-[#8BAA9E] rounded-2xl py-4"
              data-testid="add-goal-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {goals.length === 0 ? 'Create Your First Goal' : 'Add Another Goal'}
            </Button>
          )}

          {!user?.is_premium && goals.length >= 1 && (
            <p className="text-center text-[#7A7670] text-xs font-sans mt-4">
              <Crown className="w-3 h-3 inline mr-1 text-[#C6A75E]" />
              Upgrade to Premium for unlimited goals
            </p>
          )}
        </div>

        {/* Daily reminder — device notification, iPhone/Android app only */}
        {isNative() && (
        <div className="bg-[#252528] border border-white/5 rounded-[24px] overflow-hidden" data-testid="daily-reminder-card">
          <div className="p-5 border-b border-white/5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#8BAA9E]/10 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-[#8BAA9E]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-[#DCD3CA] font-sans" id="reminders-toggle-label">
                    Daily reminder
                  </p>
                  <p className="text-[#7A7670] text-xs font-sans">
                    A gentle daily notification on this device.
                  </p>
                </div>
              </div>
              <Switch
                checked={reminderSettings.enabled}
                onCheckedChange={handleReminderToggle}
                disabled={savingReminderToggle}
                aria-labelledby="reminders-toggle-label"
                data-testid="reminders-switch"
                className="!h-7 !w-12 !border-transparent data-[state=checked]:!bg-[#8BAA9E] data-[state=unchecked]:!bg-[#3E3E42] shadow-inner [&>span]:!h-5 [&>span]:!w-5 [&>span]:!bg-white [&>span]:shadow-md [&>span]:data-[state=checked]:!translate-x-5 [&>span]:data-[state=unchecked]:!translate-x-0.5"
              />
            </div>
          </div>

          {reminderSettings.enabled && (
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-[#B5B1AB]" />
                  <span className="text-[#B5B1AB] text-sm font-sans">Reminder Time</span>
                </div>
                <Input
                  type="time"
                  value={reminderSettings.time}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  className="w-32 bg-[#1C1C1E] border-white/10 rounded-xl text-[#DCD3CA] text-center"
                  data-testid="reminder-time-input"
                />
              </div>
            </div>
          )}
        </div>
        )}

        <SignInMethodsSection />

        {/* Future Self Premium */}
        <SubscriptionSection />

        {/* Logout */}
        <Button
          variant="outline"
          onClick={handleLogout}
          className="w-full border-[#CF6679]/30 text-[#CF6679] hover:bg-[#CF6679]/10 rounded-[20px] py-5 flex items-center justify-center gap-2"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          Sign Out
        </Button>

        {/* Delete Account (GDPR) — placed near the bottom of Settings, below Sign Out */}
        <DeleteAccountSection />

        {/* Legal — required for App Store / Google Play submission.
            Privacy Policy + Terms of Use open in the system browser.
            Community Guidelines were removed alongside The Collective on 2026-07-25. */}
        <LegalLinks />

        {/* Optional analytics — GDPR: user can change their choice at any time.
            Declining does not affect any core functionality. */}
        <div
          className="mt-2 flex items-center justify-between rounded-xl bg-[#252528] px-4 py-3"
          data-testid="analytics-consent-toggle-row"
        >
          <div className="pr-4">
            <p className="text-sm text-[#F5F3EF] font-sans">Optional analytics</p>
            <p className="text-xs text-[#7A7670] font-sans mt-1">
              Helps us improve the app. No name, email or personal content is ever sent.
            </p>
          </div>
          <Switch
            data-testid="analytics-consent-toggle"
            checked={analyticsConsent === 'granted'}
            onCheckedChange={(checked) => {
              const next = checked ? 'granted' : 'declined';
              setAnalyticsConsent(next);
              setAnalyticsConsentState(next);
              toast.success(checked ? 'Analytics enabled' : 'Analytics disabled');
            }}
          />
        </div>

        {/* App Info */}
        <div className="text-center py-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Leaf className="w-4 h-4 text-[#8BAA9E]" strokeWidth={1.5} />
            <span className="text-[#7A7670] text-sm font-sans">FutureSelf</span>
          </div>
          <p className="text-[#7A7670] text-xs font-sans">Version 1.1.0</p>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav
        className="fixed left-4 right-4 bg-[#1C1C1E]/90 backdrop-blur-xl border border-white/10 rounded-full h-16 flex items-center justify-around px-6 shadow-2xl z-50"
        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex flex-col items-center gap-1 text-[#7A7670] hover:text-[#B5B1AB]"
          data-testid="nav-home"
        >
          <Leaf className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-sans">Home</span>
        </button>
        <button 
          onClick={() => navigate('/progress')}
          className="flex flex-col items-center gap-1 text-[#7A7670] hover:text-[#B5B1AB]"
          data-testid="nav-progress"
        >
          <BarChart3 className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-sans">Progress</span>
        </button>
        <button 
          className="flex flex-col items-center gap-1 text-[#8BAA9E]"
          data-testid="nav-settings"
        >
          <SettingsIcon className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-sans">Settings</span>
        </button>
      </nav>
    </div>
  );
};

export default Settings;
